import { Component, Inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { COMMONS_ENV, NetworkService } from '@damofire/data-angular-commons';
import { Division, PartialDivision, PreferenceService } from '@damofire/data-stas';
import { Warehouse } from '@damofire/data-watson';
import { TranslatePipe } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../auto-unsubscribe.decorator';
import { MenuItemComponent, NavItem } from '../menu-item/menu-item.component';
import { WarehouseSelectorService } from './warehouse-selector.service';
import { LoaderComponent } from '../loader/loader.component';
import { MatTooltip } from '@angular/material/tooltip';

// Remove items unavailable offline
function pruneNavItems(items: NavItem[], offlineWarehouses: Warehouse[] = []): NavItem[] {
  const pruned: NavItem[] = [];
  items?.forEach((item) => {
    if (item.children) {
      item.children = pruneNavItems(item.children, offlineWarehouses);
    }

    const orgWithChildren = item.dataType === 'organization' && item.children.length > 0;
    const offlineWarehouse = item.dataType === 'division' && offlineWarehouses.find((w) => w.name === item.displayName);

    if (offlineWarehouse || orgWithChildren) {
      pruned.push(item);
    }
  });

  return pruned;
}

@AutoUnsubscribe()
@Component({
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule, TranslatePipe, MenuItemComponent, MatTooltip, LoaderComponent],
  selector: 'app-warehouse-selector',
  templateUrl: './warehouse-selector.component.html',
  standalone: true,
  styleUrls: ['./warehouse-selector.component.scss'],
})
export class WarehouseSelectorComponent implements OnInit {
  _warehouse: Warehouse | Division;

  private _path: string;

  navItems: NavItem[] = [];
  menuItem: NavItem;

  private warehouses: Warehouse[] = [];
  private readonly subsink = new SubSink();
  private offline = false;
  private offlineWarehouses: PartialDivision[] = [];

  loading = false;
  loadingInterval: number;

  constructor(
    private readonly warehouseSelectorService: WarehouseSelectorService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly networkService: NetworkService,
    private readonly preferenceService: PreferenceService,
    @Inject(COMMONS_ENV) private readonly environment: any,
  ) {}

  ngOnInit(): void {
    this.subsink.add(
      this.warehouseSelectorService.$warehouses.subscribe((links: Warehouse[]) => {
        this.warehouses = links;
        this.buildNavItems(links);
      }),
      this.networkService.status.subscribe(async (connected) => {
        this.offline = !connected;
        // If offline, prune unavailable warehouses
        if (this.offline) {
          this.navItems = pruneNavItems(this.navItems, this.offlineWarehouses);
        } else {
          // Otherwise rebuild list when coming back online
          this.buildNavItems(this.warehouses);
        }
      }),
      this.preferenceService.offlineWarehouses().subscribe((changes) => {
        this.offlineWarehouses = changes.map((change) => change.value);
        this.updateOfflineItem(this.navItems);
      }),
    );
  }

  navigateTo(navItem: NavItem): void {
    this.loadingInterval = window.setTimeout(() => (this.loading = true), 500);
    this.router.navigate([navItem.route]).catch(console.error);
  }

  @Input()
  set warehouse(w: Warehouse) {
    clearInterval(this.loadingInterval);

    this._warehouse = w;
    this.loading = false;
    if (this.navItems) {
      this.updateActiveItem(this.navItems);
    }
  }

  get warehouse(): Warehouse {
    return this._warehouse;
  }

  @Input()
  set path(p: string) {
    this._path = p;

    if (this.warehouses && this.navItems) {
      this.buildNavItems(this.warehouses);
    }
  }

  get path(): string {
    return this._path;
  }

  private buildNavItems(warehouses: Warehouse[]): void {
    let navItems = this.navItemsFromWarehouses(warehouses);

    // If we have only one organization, show only the warehouses
    if (navItems.length === 1 && navItems[0].children?.length > 1) {
      navItems = navItems[0].children;
      this.menuItem = { title: 'stax.nav.divisions', titleIcon: 'business' };
    } else {
      this.menuItem = { title: 'stax.nav.organizations', titleIcon: 'device_hub' };
    }

    // Ghetto: Wasn't able to do in the loop above
    this.navItems = navItems?.map((item: NavItem) => {
      if (this.environment.offline) {
        const hasOfflineDivisions = item.children?.some((navItem) => !!this.offlineWarehouses?.find((w) => w.name === navItem.displayName));
        if (hasOfflineDivisions) {
          return { ...item, icon: 'cloud_off' };
        }
      }
      return item;
    });
  }

  private navItemsFromWarehouses(warehouses: Warehouse[]): NavItem[] {
    const navItems: NavItem[] = [];

    for (const link of warehouses) {
      const existingOrg = navItems.find((navItem) => navItem.displayName === link.organization.name);
      const divisionItem: NavItem = {
        displayName: link.name,
        route: this.path.replace(':warehouse', link._id),
        dataType: 'division',
        dataId: link._id,
        active: this._warehouse._id === link._id,
      };

      if (this.environment.offline && this.offlineWarehouses?.find((w) => w._id === link._id)) {
        divisionItem.icon = 'cloud_off';
      }

      if (existingOrg) {
        existingOrg.children.push(divisionItem);
      } else {
        // Ideally I would do the check at this level for adding an icon to the navitems object, but due to a weird bug, it always comes out as null
        navItems.push({ displayName: link.organization.name, title: 'stax.nav.divisions', titleIcon: 'business', children: [divisionItem], dataType: 'organization' });
      }
    }
    return navItems;
  }

  private updateActiveItem(navItems: NavItem[]): void {
    navItems.forEach((ni) => {
      ni.active = ni.dataId === this._warehouse._id;
      if (ni.children) {
        this.updateActiveItem(ni.children);
      }
    });
  }

  private updateOfflineItem(navItems: NavItem[]): boolean {
    let offlineItem = false;
    navItems.forEach((ni) => {
      ni.icon = null;
      const isOffline = this.environment.offline && this.offlineWarehouses?.some((w) => w._id === ni.dataId);
      if (isOffline) {
        ni.icon = 'cloud_off';
      }

      if (ni.children) {
        const childIsOffline = this.updateOfflineItem(ni.children);
        if (childIsOffline) {
          ni.icon = 'cloud_off';
        }
      }

      offlineItem = offlineItem || isOffline;
    });

    return offlineItem;
  }
}
